import { Link } from 'react-router-dom'

import { LIVE_LABEL } from './freshness.js'
import { ONLY_YOU, onlyYouCanSee } from '../trips/people.js'

// The portal's other panels. P6-83.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 TRIPS GETS `LIVE`, NOT AN AS-OF STAMP — AND THAT IS NOT A STYLE CHOICE
// ═══════════════════════════════════════════════════════════════════════════
//
// Every projected module on this page carries the reconcile stamp, because spec
// §6 says a member seeing last week's rating with no date reports a bug that is
// actually a label. The trip panel is the exception, in the other direction: it
// is a direct read of the trip service through this site's own Functions
// (ADR-0005), not a projection, so it is as current as the request that fetched
// it. A stamp there would claim a staleness it does not have — the same class of
// error as the missing stamp, pointing the other way.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A STUB PANEL SAYS WHAT THE FEATURE IS. IT NEVER SAYS HOW IT IS GOING.
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's stub drafts carried numbers — "212 in the canon", "No games
// tonight", "Five-a-side, Saturday". Spec §3 is explicit that v1 publishes movie
// data only and names music and the rest as LATER consumers, so not one of those
// has a source. The heading and the paragraph come from the command catalog,
// which is generated from the live cog surface, and everything else is a link to
// the page that already documents the feature.

/**
 * Your trips, live.
 *
 * ⚠️ IT DOES NOT WRITE, AND THERE IS NO FORM HERE. ADR-0005 D2 binds the admin
 * console and the same boundary applies to the portal: this page reads. "Start a
 * trip" is a LINK to /trips, where the form already lives and where somebody who
 * follows it can see what they are about to create — a second create form on a
 * summary panel would be a second write path to reason about for no gain.
 */
export function TripsPanel({ trips }) {
  return (
    <section className="d2d-portal__panel" aria-labelledby="portal-trips" data-testid="trips-panel">
      <div className="d2d-portal__panelhead">
        <h2 id="portal-trips" className="d2d-portal__paneltitle d2d-micro">
          Your trips
        </h2>
        <span className="d2d-portal__stamp d2d-portal__stamp--live d2d-micro">
          {trips.status === 'ready' ? LIVE_LABEL : trips.status === 'error' ? 'Unavailable' : ''}
        </span>
      </div>

      {trips.status === 'loading' ? (
        <p className="d2d-portal__flat">Loading your trips…</p>
      ) : trips.status === 'error' ? (
        // The board's own sentence is not reused: `BoardError` carries the trip
        // service's message, which belongs on the screen whose subject it is.
        // Here it is one module of five, and its failure is not the page's.
        <p className="d2d-portal__flat" data-testid="portal-trips-error">
          Trips are unavailable right now. Everything else on this page is unaffected.
        </p>
      ) : trips.list.length === 0 ? (
        <>
          {/* Verbatim from src/routes/Trips.jsx. One sentence for one state,
              wherever it is shown — a second wording is a second answer. */}
          <p className="d2d-portal__flat" data-testid="portal-trips-empty">
            No trips yet. Somebody has to start one.
          </p>
          <Link className="d2d-portal__cta" to="/trips">
            Start a trip
          </Link>
        </>
      ) : (
        <>
          <ul className="d2d-portal__triplist" data-testid="portal-trips-list">
            {trips.list.map((trip) => (
              <li key={trip.id}>
                <span className="d2d-portal__tripmain">
                  <Link className="d2d-portal__triplink" to={`/trips/${trip.id}`}>
                    {trip.title}
                  </Link>
                  <span className="d2d-portal__tripmeta d2d-micro">
                    {trip.headcount} {trip.headcount === 1 ? 'person' : 'people'}
                    {/* P6-93's fact, carried wherever a trip is listed. A member
                        with three trips nobody else can see should not have to
                        open all three to find that out, and this list is now a
                        second place they might see them. */}
                    {onlyYouCanSee(trip.visibility, trip.headcount) ? ` · ${ONLY_YOU}` : null}
                  </span>
                </span>
                <span className="d2d-portal__tripstate d2d-micro">{trip.state}</span>
              </li>
            ))}
          </ul>
          <Link className="d2d-portal__more d2d-micro" to="/trips">
            All trips →
          </Link>
        </>
      )}
    </section>
  )
}

/**
 * A feature that is real, runs in Discord, and has nothing published here.
 *
 * `row` is the command catalog's own feature row — generated from the cog
 * surface, so the label and the blurb move when the bot does and cannot describe
 * a feature that is no longer registered.
 *
 * ⚠️ THE STAMP SLOT SAYS `Nothing published`, WHICH IS THE HONEST ANSWER RATHER
 * THAN AN EMPTY ONE. freshness.js's rule: no date available, say so in words —
 * never render a stamp with no value behind it. A blank slot where every other
 * panel has a freshness label reads as a stamp that failed to load.
 */
export function StubPanel({ branch, row, docPath }) {
  const heading = row?.label ?? branch.label
  return (
    <section
      className="d2d-portal__panel"
      aria-labelledby={`portal-${branch.id}`}
      data-testid={`stub-panel-${branch.id}`}
    >
      <div className="d2d-portal__panelhead">
        <h2 id={`portal-${branch.id}`} className="d2d-portal__paneltitle d2d-micro">
          {heading}
        </h2>
        <span className="d2d-portal__stamp d2d-micro">Nothing published</span>
      </div>
      {row?.blurb ? <p className="d2d-portal__headline">{row.blurb}</p> : null}
      {/* 🔴 P6-316. THIS SENTENCE SAYS ONLY WHAT IS TRUE OF THIS BRANCH. It used to
          go on: *"— movie night is the only feature publishing to the site so far."*
          That was a claim about OTHER features, so it went false once watch and
          music started publishing, and nothing noticed: no test of this panel reads
          what is true of another one. A claim about somebody else's status rots on
          their schedule, not this panel's — so there is none to keep current.
          `doc-links.test.jsx` fails a sentence here that names another feature
          or ranks one, whatever the wording. */}
      <p className="d2d-portal__flat">It runs in Discord. Nothing from it is on this page yet.</p>
      {/* 🔴 P6-316 (REVIEW). THE WORDS ARE THE BRANCH'S `docLabel`, NEVER BUILT HERE.
          This used to read `How {heading.toLowerCase()} works →`, which rendered
          *How events works →* and *How nba scores & stats works →* on the live site;
          the design gives each stub its own, *How events work* and *How hoops works*,
          and they sit beside the branch in `branches.js`. NO FALLBACK, ON PURPOSE: a
          branch with a docs page and no label draws no link rather than a built one,
          and `doc-links.test.jsx` fails loudly for it — a fallback would hide it. */}
      {docPath && branch.docLabel ? (
        <Link className="d2d-portal__more d2d-micro" to={docPath}>
          {branch.docLabel} →
        </Link>
      ) : null}
    </section>
  )
}
